import type { SafetyAreaTag, SafetyIssue } from './types';

interface Point {
  x: number;
  y: number;
}

const isPointOnSegment = (point: Point, start: number[], end: number[]) => {
  const [startX, startY] = start;
  const [endX, endY] = end;
  const cross = (point.y - startY) * (endX - startX) - (point.x - startX) * (endY - startY);
  if (Math.abs(cross) > 1e-6) return false;

  const dot = (point.x - startX) * (endX - startX) + (point.y - startY) * (endY - startY);
  if (dot < 0) return false;

  const squaredLength = (endX - startX) ** 2 + (endY - startY) ** 2;
  return dot <= squaredLength;
};

export const pointInPolygon = (point: Point, polygon: number[][] | null) => {
  if (!polygon || polygon.length < 3) return false;

  let inside = false;
  for (let index = 0, previous = polygon.length - 1; index < polygon.length; previous = index++) {
    const currentPoint = polygon[index];
    const previousPoint = polygon[previous];

    if (isPointOnSegment(point, previousPoint, currentPoint)) return true;

    const [currentX, currentY] = currentPoint;
    const [previousX, previousY] = previousPoint;
    const intersects =
      currentY > point.y !== previousY > point.y &&
      point.x <
        ((previousX - currentX) * (point.y - currentY)) / (previousY - currentY || Number.EPSILON) +
          currentX;
    if (intersects) inside = !inside;
  }

  return inside;
};

export const findContainingAreaTag = (areaTags: SafetyAreaTag[], point: Point) =>
  areaTags.find((areaTag) => pointInPolygon(point, areaTag.coordinates));

export const safetyPinKey = (x: number, y: number) => `${x.toFixed(6)}:${y.toFixed(6)}`;

export const groupSafetyIssuesByPin = (issues: SafetyIssue[]) => {
  const groups = new Map<string, SafetyIssue[]>();
  for (const issue of issues) {
    if (issue.position_x == null || issue.position_y == null) continue;
    const key = safetyPinKey(issue.position_x, issue.position_y);
    groups.set(key, [...(groups.get(key) ?? []), issue]);
  }
  return [...groups.values()];
};
